import { CHAT_PROMPT_CONTEXT_ENTITIES } from './entities.js';
import { CHAT_PROMPT_CONTEXT_RESOURCES } from './resources.js';
import {
  CHAT_PROMPT_CONTEXT_MAX_ENTITIES,
  CHAT_PROMPT_CONTEXT_MAX_LABEL_CHARS,
  CHAT_PROMPT_CONTEXT_MAX_RESOURCES,
  CHAT_PROMPT_CONTEXT_METADATA_KEY,
  type ChatPromptContext,
  type ChatPromptContextEntity,
  type ChatPromptContextResource,
} from './types.js';

const HEADER =
  'Contexto da tela (o usuário está olhando isto agora; use estas referências para buscar os dados via tools):';

const RESOURCES_HEADER = 'Disponível neste contexto:';

function truncate(value: string, max: number): string {
  const clean = value.replaceAll(/\s+/gu, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}

function renderEntity(entity: ChatPromptContextEntity): string | null {
  const { idKey, label: name } = CHAT_PROMPT_CONTEXT_ENTITIES[entity.type];
  const label = entity.label ? truncate(entity.label, CHAT_PROMPT_CONTEXT_MAX_LABEL_CHARS) : '';

  // `screen` não tem id: só existe como rótulo.
  if (entity.type === 'screen') {
    return label ? `- ${name}: ${label}` : null;
  }

  if (!entity.id) return null;

  const reference = `${name} (${idKey}: ${entity.id})`;
  return label ? `- ${reference}: ${label}` : `- ${reference}`;
}

/**
 * Um recurso só entra no bloco com id: sem ele a Iara não tem como abrir o
 * recurso, e a linha viraria só um aviso de que algo existe.
 */
function renderResource(resource: ChatPromptContextResource): string | null {
  if (!resource.id) return null;

  const { idKey, instruction, label } = CHAT_PROMPT_CONTEXT_RESOURCES[resource.type];

  return `- ${label} (${idKey}: ${resource.id}): ${instruction}`;
}

/**
 * Renderiza o contexto da tela como bloco de texto anexável ao prompt do turno.
 * Espelha `renderAutomationContext` do caminho headless: só referências +
 * rótulos, com instrução explícita para a Iara buscar o detalhe via tools.
 *
 * Vai no prompt do TURNO (não no system prompt) de propósito — o contexto muda
 * quando o usuário navega, e mutar o system prompt invalidaria o cache do Bedrock.
 */
export function renderChatPromptContext(context: ChatPromptContext): string {
  const screenEntity: ChatPromptContextEntity[] = context.screen
    ? [{ label: context.screen, type: 'screen' }]
    : [];

  const lines = [...screenEntity, ...(context.entities ?? [])]
    .slice(0, CHAT_PROMPT_CONTEXT_MAX_ENTITIES)
    .map((entity) => renderEntity(entity))
    .filter((line): line is string => line !== null);

  if (lines.length === 0) return '';

  const resourceLines = (context.resources ?? [])
    .slice(0, CHAT_PROMPT_CONTEXT_MAX_RESOURCES)
    .map((resource) => renderResource(resource))
    .filter((line): line is string => line !== null);

  // Recursos são uma seção à parte, separada por linha em branco: "o que existe
  // aqui" não se mistura com "onde o usuário está". Sem recursos, o bloco fica
  // idêntico ao de antes.
  const resourcesBlock = resourceLines.length > 0 ? ['', RESOURCES_HEADER, ...resourceLines] : [];

  return ['---', HEADER, ...lines, ...resourcesBlock].join('\n');
}

/**
 * Concatena o texto do usuário com o bloco de contexto. Identidade quando não
 * há contexto útil.
 */
export function buildPromptWithContext(
  content: string,
  context?: ChatPromptContext | null,
): string {
  if (!context) return content;

  const block = renderChatPromptContext(context);
  return block ? `${content}\n\n${block}` : content;
}

/**
 * Lê o contexto de `chat_messages.metadata`, tolerando ausência e formato inválido.
 */
export function readChatPromptContext(metadata: unknown): ChatPromptContext | undefined {
  if (!metadata || typeof metadata !== 'object') return undefined;

  const candidate = (metadata as Record<string, unknown>)[CHAT_PROMPT_CONTEXT_METADATA_KEY];
  if (!candidate || typeof candidate !== 'object') return undefined;

  const { entities } = candidate as { entities?: unknown };
  if (!Array.isArray(entities)) return undefined;

  return candidate as ChatPromptContext;
}
